'use client';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import AppLogo from '@/components/ui/AppLogo';
import MuteToggle from '@/components/MuteToggle';

// ─── Constants ────────────────────────────────────────────────────────────────

const COSMETIC_META: Record<string, { label: string; emoji: string; color: string; rgb: string; tier: number }> = {
  spark_badge: { label: 'Spark Badge', emoji: '✨', color: '#ffd23f', rgb: '255,210,63', tier: 1 },
  flame_badge: { label: 'Flame Badge', emoji: '🔥', color: '#ff9500', rgb: '255,149,0', tier: 2 },
  crown_silver: { label: 'Silver Crown', emoji: '👑', color: '#94a3b8', rgb: '148,163,184', tier: 3 },
  crown_gold: { label: 'Gold Crown', emoji: '👑', color: '#ffd23f', rgb: '255,210,63', tier: 4 },
};

const MILESTONES = [
  { count: 1, reward: '7 free premium days', cosmetic: 'spark_badge', emoji: '✨', label: 'Spark Badge' },
  { count: 2, reward: '14 free premium days', cosmetic: 'flame_badge', emoji: '🔥', label: 'Flame Badge' },
  { count: 5, reward: '35 free premium days', cosmetic: 'crown_silver', emoji: '👑', label: 'Silver Crown' },
  { count: 10, reward: '70 free premium days', cosmetic: 'crown_gold', emoji: '👑', label: 'Gold Crown' },
];

const COSMETIC_RANK: Record<string, number> = {
  crown_gold: 4,
  crown_silver: 3,
  flame_badge: 2,
  spark_badge: 1,
};

// ─── Types ────────────────────────────────────────────────────────────────────

interface HubStats {
  referralCode: string;
  totalReferrals: number;
  premiumDaysEarned: number;
  premiumExpiresAt: string | null;
  cosmeticBadge: string | null;
  allBadgesEarned: string[];
  recentReferrals: { createdAt: string; rewardDays: number; cosmeticUnlocked: string | null }[];
}

interface LeaderboardEntry {
  id: string;
  full_name: string;
  referral_count: number;
  premium_days_earned: number;
  cosmetic_badge: string | null;
  is_premium: boolean;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatPill({
  icon,
  value,
  label,
  color,
  rgb,
}: {
  icon: string;
  value: string | number;
  label: string;
  color: string;
  rgb: string;
}) {
  return (
    <div
      className="flex-1 rounded-2xl p-4 flex flex-col items-center gap-1 min-w-0"
      style={{ background: `rgba(${rgb}, 0.07)`, border: `1px solid rgba(${rgb}, 0.2)` }}
    >
      <span className="text-2xl">{icon}</span>
      <span className="text-xl font-black" style={{ color }}>
        {value}
      </span>
      <span className="text-xs text-muted-foreground text-center leading-tight font-medium">{label}</span>
    </div>
  );
}

function BadgeGrid({ earned, total }: { earned: string[]; total: typeof MILESTONES }) {
  return (
    <div className="grid grid-cols-4 gap-2">
      {total.map((m) => {
        const isEarned = earned.includes(m.cosmetic);
        const meta = COSMETIC_META[m.cosmetic];
        return (
          <div
            key={m.cosmetic}
            className="flex flex-col items-center gap-1.5 rounded-2xl p-3 transition-all"
            style={{
              background: isEarned
                ? `rgba(${meta.rgb}, 0.1)`
                : 'rgba(255,255,255,0.03)',
              border: isEarned
                ? `1px solid rgba(${meta.rgb}, 0.35)`
                : '1px solid rgba(255,255,255,0.06)',
              opacity: isEarned ? 1 : 0.45,
            }}
          >
            <span className="text-2xl" style={{ filter: isEarned ? 'none' : 'grayscale(1)' }}>
              {m.emoji}
            </span>
            <span
              className="text-center font-bold leading-tight"
              style={{ fontSize: '0.6rem', color: isEarned ? meta.color : 'var(--muted-foreground)' }}
            >
              {m.label}
            </span>
            {isEarned && (
              <span
                className="text-center font-black uppercase tracking-wide"
                style={{ fontSize: '0.5rem', color: '#36e2a4' }}
              >
                Earned
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

function PremiumDaysCard({
  daysEarned,
  expiresAt,
}: {
  daysEarned: number;
  expiresAt: string | null;
}) {
  const isPremiumActive = expiresAt ? new Date(expiresAt) > new Date() : false;
  const daysLeft = expiresAt
    ? Math.max(0, Math.ceil((new Date(expiresAt).getTime() - Date.now()) / 86400000))
    : 0;

  return (
    <div
      className="w-full rounded-2xl overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, rgba(22,22,42,0.95), rgba(15,15,30,0.98))',
        border: '1px solid rgba(255,210,63,0.25)',
        boxShadow: '0 0 30px rgba(255,210,63,0.05)',
      }}
    >
      <div className="h-0.5 w-full" style={{ background: 'linear-gradient(90deg, #ffd23f 0%, #ff9500 60%, #ff3b6b 100%)' }} />
      <div className="p-5 flex items-center gap-4">
        <div
          className="w-14 h-14 rounded-2xl flex items-center justify-center text-2xl flex-shrink-0"
          style={{ background: 'rgba(255,210,63,0.12)', border: '1px solid rgba(255,210,63,0.3)' }}
        >
          ⭐
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-black uppercase tracking-widest text-muted-foreground mb-1">Premium Days Balance</p>
          <div className="flex items-baseline gap-2">
            <span
              className="text-3xl font-black"
              style={{
                background: 'linear-gradient(135deg, #ffd23f 0%, #ff9500 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              {daysEarned}
            </span>
            <span className="text-sm text-muted-foreground font-medium">days earned total</span>
          </div>
          {isPremiumActive ? (
            <p className="text-xs font-semibold mt-1" style={{ color: '#36e2a4' }}>
              ✓ Active — {daysLeft} day{daysLeft !== 1 ? 's' : ''} remaining
            </p>
          ) : (
            <p className="text-xs text-muted-foreground font-medium mt-1">
              Invite friends to earn more days
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function RankCallout({
  rank,
  total,
  loading,
}: {
  rank: number;
  total: number;
  loading: boolean;
}) {
  if (loading) {
    return (
      <div
        className="w-full rounded-2xl p-4 flex items-center gap-3"
        style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}
      >
        <div className="w-5 h-5 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: 'rgba(54,226,164,0.5)', borderTopColor: 'transparent' }} />
        <span className="text-xs text-muted-foreground font-medium">Loading your rank…</span>
      </div>
    );
  }

  if (rank === 0) {
    return (
      <div
        className="w-full rounded-2xl p-4 flex items-center gap-3"
        style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}
      >
        <span className="text-xl">🌱</span>
        <div className="flex-1">
          <p className="text-xs font-black text-foreground">Not on the leaderboard yet</p>
          <p className="text-xs text-muted-foreground font-medium">Invite one friend to claim your spot</p>
        </div>
      </div>
    );
  }

  const rankEmoji = rank === 1 ? '🥇' : rank === 2 ? '🥈' : rank === 3 ? '🥉' : '🎯';
  const rankColor = rank <= 3 ? '#ffd23f' : '#36e2a4';

  return (
    <div
      className="w-full rounded-2xl p-4 flex items-center gap-4"
      style={{
        background: rank <= 3 ? 'rgba(255,210,63,0.07)' : 'rgba(54,226,164,0.07)',
        border: `1px solid ${rank <= 3 ? 'rgba(255,210,63,0.25)' : 'rgba(54,226,164,0.25)'}`,
      }}
    >
      <span className="text-3xl flex-shrink-0">{rankEmoji}</span>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-black uppercase tracking-widest text-muted-foreground">Your Leaderboard Rank</p>
        <div className="flex items-baseline gap-2 mt-0.5">
          <span className="text-2xl font-black" style={{ color: rankColor }}>
            #{rank}
          </span>
          <span className="text-xs text-muted-foreground font-medium">of {total} referrers</span>
        </div>
      </div>
      <div
        className="flex-shrink-0 px-3 py-1.5 rounded-xl text-xs font-black"
        style={{
          background: rank <= 3 ? 'rgba(255,210,63,0.12)' : 'rgba(54,226,164,0.12)',
          border: `1px solid ${rank <= 3 ? 'rgba(255,210,63,0.3)' : 'rgba(54,226,164,0.3)'}`,
          color: rankColor,
        }}
      >
        Top {Math.ceil((rank / Math.max(total, 1)) * 100)}%
      </div>
    </div>
  );
}

// ─── Auth Gate ────────────────────────────────────────────────────────────────

function AuthGate() {
  const router = useRouter();
  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 py-12">
      <div className="w-full max-w-sm text-center">
        <div className="text-5xl mb-4">🔗</div>
        <h1 className="text-2xl font-black text-foreground mb-2">Referral Hub</h1>
        <p className="text-muted-foreground text-sm mb-6">Sign in to see your invite link, badges, and rank.</p>
        <button
          onClick={() => router.push('/profile')}
          className="btn-primary px-8 py-3 text-sm font-black"
        >
          Sign In to Continue →
        </button>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function ReferralHubClient() {
  const router = useRouter();
  const { user } = useAuth();

  const [stats, setStats] = useState<HubStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [lbEntries, setLbEntries] = useState<LeaderboardEntry[]>([]);
  const [lbLoading, setLbLoading] = useState(true);
  const prevReferralCount = useRef<number | null>(null);

  const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || 'https://unbored3102.builtwithrocket.new';

  // ── Load user stats ──────────────────────────────────────────────────────
  const loadStats = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    const supabase = createClient();
    try {
      const { data: codeData } = await supabase.rpc('ensure_referral_code', { p_user_id: user.id });
      const referralCode = codeData as string;

      const { data: profile } = await supabase
        .from('user_profiles')
        .select('referral_code, premium_days_earned, premium_expires_at, cosmetic_badge')
        .eq('id', user.id)
        .single();

      const { data: referrals } = await supabase
        .from('referrals')
        .select('created_at, reward_days, cosmetic_unlocked')
        .eq('referrer_id', user.id)
        .eq('status', 'rewarded')
        .order('created_at', { ascending: false })
        .limit(10);

      const newCount = referrals?.length || 0;
      prevReferralCount.current = newCount;

      // Derive all badges earned from milestones crossed
      const allBadgesEarned = MILESTONES
        .filter(m => newCount >= m.count)
        .map(m => m.cosmetic);

      setStats({
        referralCode: profile?.referral_code || referralCode || '',
        totalReferrals: newCount,
        premiumDaysEarned: profile?.premium_days_earned || 0,
        premiumExpiresAt: profile?.premium_expires_at || null,
        cosmeticBadge: profile?.cosmetic_badge || null,
        allBadgesEarned,
        recentReferrals: (referrals || []).map(r => ({
          createdAt: r.created_at,
          rewardDays: r.reward_days,
          cosmeticUnlocked: r.cosmetic_unlocked,
        })),
      });
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, [user]);

  // ── Load leaderboard ─────────────────────────────────────────────────────
  const loadLeaderboard = useCallback(async () => {
    const supabase = createClient();
    try {
      const { data: referralCounts } = await supabase
        .from('referrals')
        .select('referrer_id')
        .eq('status', 'rewarded');

      const countMap: Record<string, number> = {};
      (referralCounts || []).forEach(r => {
        countMap[r.referrer_id] = (countMap[r.referrer_id] || 0) + 1;
      });

      const referrerIds = Object.keys(countMap);
      if (referrerIds.length === 0) { setLbEntries([]); setLbLoading(false); return; }

      const { data: profiles } = await supabase
        .from('user_profiles')
        .select('id, full_name, premium_days_earned, cosmetic_badge, is_premium')
        .in('id', referrerIds);

      const merged: LeaderboardEntry[] = (profiles || []).map(p => ({
        id: p.id,
        full_name: p.full_name || 'Anonymous',
        referral_count: countMap[p.id] || 0,
        premium_days_earned: p.premium_days_earned || 0,
        cosmetic_badge: p.cosmetic_badge || null,
        is_premium: p.is_premium || false,
      }));

      setLbEntries(merged.sort((a, b) => b.referral_count - a.referral_count));
    } catch {
      // silently fail
    } finally {
      setLbLoading(false);
    }
  }, []);

  useEffect(() => { loadStats(); loadLeaderboard(); }, [loadStats, loadLeaderboard]);

  // Real-time referral subscription
  useEffect(() => {
    if (!user) return;
    const supabase = createClient();
    const channel = supabase
      .channel(`hub-referrals-${user.id}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'referrals', filter: `referrer_id=eq.${user.id}` }, () => {
        loadStats();
        loadLeaderboard();
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user, loadStats, loadLeaderboard]);

  const inviteLink = stats?.referralCode ? `${siteUrl}/?ref=${stats.referralCode}` : '';

  const handleCopy = async () => {
    if (!inviteLink) return;
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* fallback */ }
  };

  const handleShare = async () => {
    if (!inviteLink) return;
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({
          title: 'Join Unbored — Free Premium Days',
          text: '🎮 I\'m playing Unbored — micro-games engineered to rewire your brain. Use my invite link and we both get free premium days!',
          url: inviteLink,
        });
      } catch { handleCopy(); }
    } else {
      handleCopy();
    }
  };

  // Derived leaderboard rank
  const currentUserRank = user ? lbEntries.findIndex(e => e.id === user.id) + 1 : 0;

  // Next milestone
  const nextMilestone = MILESTONES.find(m => (stats?.totalReferrals || 0) < m.count);
  const progressToNext = nextMilestone
    ? Math.min(((stats?.totalReferrals || 0) / nextMilestone.count) * 100, 100)
    : 100;

  if (!user) return <AuthGate />;

  return (
    <div className="relative z-10 flex flex-col min-h-screen" style={{ background: '#0a0a0f' }}>
      {/* Ambient glow */}
      <div
        className="pointer-events-none fixed inset-0 z-0"
        style={{
          background: 'radial-gradient(ellipse 70% 50% at 50% -10%, rgba(255,210,63,0.07) 0%, transparent 70%)',
        }}
      />

      {/* Header */}
      <header className="relative z-10 flex items-center justify-between px-5 pt-5 pb-3">
        <div className="flex items-center gap-2">
          <AppLogo size={28} onClick={() => router.push('/')} />
          <span
            className="font-bold text-base tracking-tight text-foreground cursor-pointer"
            onClick={() => router.push('/')}
          >
            Unbored
          </span>
        </div>
        <div className="flex items-center gap-2">
          <MuteToggle />
          <button
            onClick={() => router.push('/referral-leaderboard')}
            className="px-3 py-2 rounded-xl text-xs font-bold transition-all"
            style={{
              background: 'rgba(54,226,164,0.1)',
              border: '1px solid rgba(54,226,164,0.25)',
              color: '#36e2a4',
            }}
          >
            Full Board →
          </button>
        </div>
      </header>

      {/* Main content */}
      <main className="relative z-10 flex-1 px-5 py-4 max-w-lg mx-auto w-full flex flex-col gap-5 pb-16">

        {/* Page title */}
        <div className="text-center pt-2">
          <div
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black uppercase tracking-widest mb-3"
            style={{ background: 'rgba(255,210,63,0.1)', border: '1px solid rgba(255,210,63,0.28)', color: '#ffd23f' }}
          >
            🔗 Referral Hub
          </div>
          <h1 className="text-2xl font-extrabold text-foreground leading-tight">
            Your Viral Dashboard
          </h1>
          <p className="text-muted-foreground text-sm mt-1 font-medium">
            Invite friends · earn premium days · climb the board
          </p>
        </div>

        {/* Loading skeleton */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div
              className="w-6 h-6 rounded-full border-2 border-t-transparent animate-spin"
              style={{ borderColor: 'rgba(255,210,63,0.5)', borderTopColor: 'transparent' }}
            />
          </div>
        ) : (
          <>
            {/* ── Quick stats row ── */}
            <div className="flex gap-3">
              <StatPill
                icon="👥"
                value={stats?.totalReferrals || 0}
                label="Friends Invited"
                color="#36e2a4"
                rgb="54,226,164"
              />
              <StatPill
                icon="⭐"
                value={stats?.premiumDaysEarned || 0}
                label="Days Earned"
                color="#ffd23f"
                rgb="255,210,63"
              />
              <StatPill
                icon={stats?.cosmeticBadge ? COSMETIC_META[stats.cosmeticBadge]?.emoji || '🎖️' : '🎖️'}
                value={stats?.allBadgesEarned?.length || 0}
                label="Badges"
                color="#a78bfa"
                rgb="167,139,250"
              />
            </div>

            {/* ── Leaderboard rank ── */}
            <RankCallout
              rank={currentUserRank}
              total={lbEntries.length}
              loading={lbLoading}
            />

            {/* ── Premium days balance ── */}
            <PremiumDaysCard
              daysEarned={stats?.premiumDaysEarned || 0}
              expiresAt={stats?.premiumExpiresAt || null}
            />

            {/* ── Invite link card ── */}
            <div
              className="w-full rounded-2xl overflow-hidden"
              style={{
                background: 'linear-gradient(135deg, rgba(22,22,42,0.95), rgba(15,15,30,0.98))',
                border: '1px solid rgba(54,226,164,0.2)',
                boxShadow: '0 0 30px rgba(54,226,164,0.04)',
              }}
            >
              <div className="h-0.5 w-full" style={{ background: 'linear-gradient(90deg, #36e2a4 0%, #5b8fff 100%)' }} />
              <div className="p-5 flex flex-col gap-4">
                <div className="flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center text-xl flex-shrink-0"
                    style={{ background: 'rgba(54,226,164,0.12)', border: '1px solid rgba(54,226,164,0.3)' }}
                  >
                    🔗
                  </div>
                  <div>
                    <h3 className="font-black text-foreground text-sm leading-tight">Your Personal Invite Link</h3>
                    <p className="text-xs text-muted-foreground font-medium">Each friend who joins earns you 7 free premium days</p>
                  </div>
                </div>

                {/* Link display */}
                <div
                  className="flex items-center gap-2 rounded-xl px-3 py-2.5"
                  style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
                >
                  <span
                    className="flex-1 text-xs font-mono truncate"
                    style={{ color: 'rgba(255,255,255,0.65)' }}
                  >
                    {inviteLink || 'Generating your link…'}
                  </span>
                  <button
                    onClick={handleCopy}
                    className="flex-shrink-0 px-2.5 py-1 rounded-lg text-xs font-bold transition-all"
                    style={{
                      background: copied ? 'rgba(54,226,164,0.15)' : 'rgba(54,226,164,0.1)',
                      border: `1px solid ${copied ? 'rgba(54,226,164,0.5)' : 'rgba(54,226,164,0.25)'}`,
                      color: '#36e2a4',
                    }}
                  >
                    {copied ? '✓ Copied' : 'Copy'}
                  </button>
                </div>

                {/* Share CTA */}
                <button
                  onClick={handleShare}
                  className="w-full py-3 rounded-xl font-black text-sm tracking-wide transition-all"
                  style={{
                    background: 'linear-gradient(135deg, #36e2a4 0%, #5b8fff 100%)',
                    color: '#0a0a0f',
                    boxShadow: '0 0 24px rgba(54,226,164,0.2)',
                  }}
                >
                  🚀 Share Invite Link
                </button>
              </div>
            </div>

            {/* ── Cosmetic badges earned ── */}
            <div
              className="w-full rounded-2xl p-5"
              style={{
                background: 'linear-gradient(135deg, rgba(22,22,42,0.95), rgba(15,15,30,0.98))',
                border: '1px solid rgba(167,139,250,0.2)',
              }}
            >
              <div className="flex items-center justify-between mb-4">
                <p className="text-xs font-black uppercase tracking-widest text-muted-foreground">Cosmetic Badges</p>
                <span
                  className="text-xs font-black px-2 py-0.5 rounded-full"
                  style={{ background: 'rgba(167,139,250,0.12)', border: '1px solid rgba(167,139,250,0.3)', color: '#a78bfa' }}
                >
                  {stats?.allBadgesEarned?.length || 0} / {MILESTONES.length}
                </span>
              </div>
              <BadgeGrid
                earned={stats?.allBadgesEarned || []}
                total={MILESTONES}
              />
            </div>

            {/* ── Milestone progress ── */}
            <div
              className="w-full rounded-2xl p-5"
              style={{
                background: 'linear-gradient(135deg, rgba(22,22,42,0.9), rgba(15,15,30,0.95))',
                border: '1px solid rgba(42,42,69,0.8)',
              }}
            >
              <p className="text-xs font-black uppercase tracking-widest text-muted-foreground mb-4">Reward Milestones</p>
              <div className="flex flex-col gap-3">
                {MILESTONES.map(m => {
                  const reached = (stats?.totalReferrals || 0) >= m.count;
                  const meta = COSMETIC_META[m.cosmetic];
                  return (
                    <div key={m.count} className="flex items-center gap-3">
                      <div
                        className="w-9 h-9 rounded-xl flex items-center justify-center text-base flex-shrink-0"
                        style={{
                          background: reached ? `rgba(${meta.rgb}, 0.15)` : 'rgba(255,255,255,0.04)',
                          border: `1px solid ${reached ? `rgba(${meta.rgb}, 0.45)` : 'rgba(255,255,255,0.08)'}`,
                        }}
                      >
                        {reached ? '✓' : m.emoji}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-foreground">
                            {m.count} {m.count === 1 ? 'friend' : 'friends'}
                          </span>
                          <span
                            className="text-xs px-1.5 py-0.5 rounded font-bold"
                            style={{
                              background: reached ? 'rgba(54,226,164,0.12)' : 'rgba(255,255,255,0.05)',
                              color: reached ? '#36e2a4' : 'rgba(255,255,255,0.25)',
                              fontSize: '0.6rem',
                              letterSpacing: '0.05em',
                            }}
                          >
                            {reached ? 'UNLOCKED' : 'LOCKED'}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground">{m.reward} · {m.label}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {nextMilestone && (
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs text-muted-foreground font-medium">
                      {stats?.totalReferrals || 0} / {nextMilestone.count} to next milestone
                    </span>
                    <span className="text-xs font-black" style={{ color: '#ffd23f' }}>
                      {Math.round(progressToNext)}%
                    </span>
                  </div>
                  <div className="w-full h-1.5 rounded-full" style={{ background: 'rgba(255,255,255,0.06)' }}>
                    <div
                      className="h-full rounded-full transition-all duration-700"
                      style={{
                        width: `${progressToNext}%`,
                        background: 'linear-gradient(90deg, #ffd23f 0%, #ff9500 100%)',
                      }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* ── Recent rewards ── */}
            {(stats?.recentReferrals?.length || 0) > 0 && (
              <div
                className="w-full rounded-2xl p-5"
                style={{
                  background: 'linear-gradient(135deg, rgba(22,22,42,0.9), rgba(15,15,30,0.95))',
                  border: '1px solid rgba(42,42,69,0.8)',
                }}
              >
                <p className="text-xs font-black uppercase tracking-widest text-muted-foreground mb-3">Recent Rewards</p>
                <div className="flex flex-col gap-2">
                  {stats?.recentReferrals?.slice(0, 5).map((r, i) => {
                    const cosmetic = r.cosmeticUnlocked ? COSMETIC_META[r.cosmeticUnlocked] : null;
                    return (
                      <div key={i} className="flex items-center gap-3">
                        <span className="text-base">{cosmetic?.emoji || '🎁'}</span>
                        <div className="flex-1">
                          <span className="text-xs font-bold text-foreground">+{r.rewardDays} premium days</span>
                          {cosmetic && (
                            <span className="ml-2 text-xs font-semibold" style={{ color: cosmetic.color }}>
                              · {cosmetic.label} unlocked
                            </span>
                          )}
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {new Date(r.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── CTA to full leaderboard ── */}
            <button
              onClick={() => router.push('/referral-leaderboard')}
              className="w-full py-3.5 rounded-2xl font-black text-sm transition-all"
              style={{
                background: 'rgba(54,226,164,0.08)',
                border: '1px solid rgba(54,226,164,0.2)',
                color: '#36e2a4',
              }}
            >
              View Full Referral Leaderboard →
            </button>
          </>
        )}
      </main>
    </div>
  );
}
